/**
 * AUTO-GENERATED by gondola-design-system/build.ts — DO NOT EDIT
 *
 * Loyalty tier vocabulary shared by web (utils.tsx) and mobile (loyalty-utils.ts).
 * Source of truth: gondola-design-system/tokens/loyalty-tier.ts
 */

export interface TierKeywordRule {
  readonly keywords: readonly string[];
  readonly tier: number;
  /**
   * If set, the rule only fires when `loyaltyProgram` (case-insensitive,
   * full-name match) is one of these program-name strings. Used to scope
   * generic-keyword overrides to a specific program (e.g. Enterprise Plus'
   * Silver/Gold/Platinum tiers).
   */
  readonly programs?: readonly string[];
}

/**
 * Chain-specific overrides — checked first. Promotes top-tier statuses with
 * non-standard names (Ambassador, 1K, Globalist, Super Elite, etc.) and
 * corrects misleading keyword matches (Alaska MVP Gold 100K).
 */
export const STATUS_TIER_OVERRIDES: readonly TierKeywordRule[] = [
  { keywords: ["ambassador"], tier: 5 },
  { keywords: ["1k"], tier: 5 },
  { keywords: ["100k"], tier: 5 },
  { keywords: ["globalist"], tier: 5 },
  { keywords: ["super elite"], tier: 5 },
  { keywords: ["hon circle"], tier: 5 },
  { keywords: ["infinite prime"], tier: 5 },
  { keywords: ["limitless"], tier: 5 },
  { keywords: ["million miler"], tier: 5 },
  { keywords: ["ultimate"], tier: 5 },
  { keywords: ["concierge"], tier: 5 },
  { keywords: ["executive platinum"], tier: 5 },
  { keywords: ["a-list preferred"], tier: 5 },
  { keywords: ["morning calm premium"], tier: 4 },
  { keywords: ["75k"], tier: 4 },
  { keywords: ["infinite"], tier: 4 },
  { keywords: ["senator"], tier: 4 },
  { keywords: ["chairman's club", "chairmans club"], tier: 4 },
  { keywords: ["50k"], tier: 3 },
  { keywords: ["president's club", "presidents club"], tier: 3 },
  { keywords: ["platinum"], tier: 3, programs: ["Enterprise Plus"] },
  { keywords: ["morning calm"], tier: 2 },
  { keywords: ["35k"], tier: 2 },
  { keywords: ["frequent traveller"], tier: 2 },
  { keywords: ["preferred plus"], tier: 2 },
  { keywords: ["president's circle", "presidents circle"], tier: 2 },
  { keywords: ["executive elite"], tier: 2 },
  { keywords: ["gold"], tier: 2, programs: ["Enterprise Plus"] },
  { keywords: ["25k"], tier: 1 },
  { keywords: ["preferred"], tier: 1 },
  { keywords: ["five star"], tier: 1 },
  { keywords: ["executive"], tier: 1 },
  { keywords: ["fastbreak"], tier: 1 },
  { keywords: ["a-list"], tier: 1 },
  { keywords: ["silver"], tier: 1, programs: ["Enterprise Plus"] },
] as const;

/** Generic tier keyword fallback — applied when no override matches. */
export const TIER_STATUS_KEYWORDS: readonly TierKeywordRule[] = [
  { keywords: ["platinum", "diamond"], tier: 5 },
  { keywords: ["titanium"], tier: 4 },
  { keywords: ["gold"], tier: 3 },
  { keywords: ["silver"], tier: 2 },
  { keywords: ["bronze"], tier: 1 },
] as const;

/** Tag text color keyed by `tier-N`. Mirrors LOYALTY_TAG_TEXT_COLORS in tokens/gradients.ts. */
export const TIER_TEXT_COLORS_BY_TIER: Record<string, string> = {
  "tier-0": "#FFFFFF",
  "tier-1": "#FFFFFF",
  "tier-2": "#263548",
  "tier-3": "#263548",
  "tier-4": "#FFFFFF",
  "tier-5": "#263548",
};

/**
 * Map a loyalty status string to the tier whose name it matches.
 * Returns undefined when the status doesn't include any known tier keyword.
 *
 * Resolution order: chain-specific overrides → generic keywords → undefined.
 *
 * Matching uses `loyaltyStatus` only (NOT a prepended program name) — words
 * inside program names like "Accor Live Limitless" / "Hertz Gold Plus Rewards"
 * would otherwise collide with override/generic keywords ("limitless" / "gold")
 * and falsely promote every member of those programs. To disambiguate
 * status-name collisions across programs (e.g. Enterprise Plus' "Platinum" vs.
 * Hilton Honors' implicit "Platinum") rules can carry a `programs` filter:
 * the rule only fires when `loyaltyProgram` exactly matches one of those names.
 */
export function tierFromStatusName(
  loyaltyStatus?: string | null,
  loyaltyProgram?: string | null
): number | undefined {
  if (!loyaltyStatus) return undefined;
  const status = loyaltyStatus.toLowerCase();
  const program = loyaltyProgram ? loyaltyProgram.toLowerCase() : null;
  const programMatches = (rule: TierKeywordRule): boolean => {
    if (!rule.programs) return true;
    if (!program) return false;
    return rule.programs.some((p) => p.toLowerCase() === program);
  };
  for (const rule of STATUS_TIER_OVERRIDES) {
    if (!programMatches(rule)) continue;
    if (rule.keywords.some((k) => status.includes(k))) return rule.tier;
  }
  for (const rule of TIER_STATUS_KEYWORDS) {
    if (!programMatches(rule)) continue;
    if (rule.keywords.some((k) => status.includes(k))) return rule.tier;
  }
  return undefined;
}

/**
 * Resolve a tier index from the chain's numeric tier, falling back to
 * status-name keyword matching, defaulting to 0 when nothing matches.
 */
export function deriveProgramTierIndex(
  programTierIndex: number | undefined,
  loyaltyStatus?: string | null,
  loyaltyProgram?: string | null
): number {
  if (programTierIndex !== undefined) return programTierIndex;
  return tierFromStatusName(loyaltyStatus, loyaltyProgram) ?? 0;
}
